/**
 * StoryNode — the cream custom node rendered on the canvas.
 *
 * One component covers all three structural kinds from `build-graph`:
 *   - "scene"   — a soft card with the display-serif title + an id chip;
 *   - "ending"  — a coral-toned terminal card (全剧终 / 待续 / 坏结局);
 *   - "missing" — a dashed warning card flagging a dangling `@next` target.
 *
 * Presentational only: it reads its data from `data` and reports clicks up via
 * the node's own `onClick` (wired by ReactFlow → CanvasView's `onNodeClick`).
 * Colours come exclusively from design tokens via the `.css`.
 */
import { memo, type MouseEvent } from "react";
import { Handle, Position, type NodeProps } from "@xyflow/react";
import { AlertTriangle, Flag, MapPin, PencilLine, Plus } from "lucide-react";
import { Badge } from "../../ui/index.js";
import type { StoryNodeKind } from "../../ls/build-graph.js";
import { useTr, type Entry } from "../../i18n/context.js";

const S = {
  missingTarget: { zh: "缺失目标", en: "Missing target" },
  createTarget: { zh: "创建", en: "Create" },
  enterEpisode: { zh: "进入剧内", en: "Edit inside" },
} satisfies Record<string, Entry>;

/** Data carried on each ReactFlow node for the canvas. */
export interface StoryNodeData {
  /** Title shown in the serif display face. */
  readonly label: string;
  /** Structural kind — drives the card treatment. */
  readonly kind: StoryNodeKind;
  /** Original scene/target id (shown as a mono chip on scene/missing nodes). */
  readonly sceneId: string;
  /** Public Lunascript routing id, shown to creators. */
  readonly branchKey?: string;
  /** True when this is the currently-selected scene. */
  readonly active?: boolean;
  /** Ending flavour, for ending nodes (complete / to_be_continued / …). */
  readonly endingType?: string;
  /** True for a dangling-target placeholder. */
  readonly missing?: boolean;
  /** Target branch key represented by a missing node. */
  readonly missingTarget?: string;
  /** Create the missing target scene. */
  readonly onCreateMissing?: (branchKey: string) => void;
  /** True when the scene can open the episode-level editor. */
  readonly canDrill?: boolean;
  /** Open the episode-level editor for this scene. */
  readonly onDrill?: (sceneId: string) => void;
  [key: string]: unknown;
}

function StoryNodeImpl({ data }: NodeProps) {
  const tr = useTr();
  const d = data as StoryNodeData;
  const classes = [
    "lc-canvas-node",
    `lc-canvas-node--${d.kind}`,
    d.active ? "is-active" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const createMissing = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (d.missingTarget) d.onCreateMissing?.(d.missingTarget);
  };
  const drillScene = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    d.onDrill?.(d.sceneId);
  };

  return (
    <div className={classes} data-kind={d.kind}>
      {/* Incoming edges dock on the left, outgoing leave on the right. */}
      <Handle type="target" position={Position.Left} className="lc-canvas-handle" />

      <div className="lc-canvas-node__head">
        <span className="lc-canvas-node__icon" aria-hidden="true">
          {d.kind === "ending" ? (
            <Flag size={14} />
          ) : d.kind === "missing" ? (
            <AlertTriangle size={14} />
          ) : (
            <MapPin size={14} />
          )}
        </span>
        <span className="lc-canvas-node__title">{d.label}</span>
      </div>

      {d.kind === "scene" ? (
        <div className="lc-canvas-node__meta">
          <span className="lc-canvas-node__id">{d.branchKey ?? d.sceneId}</span>
        </div>
      ) : null}

      {d.kind === "scene" && d.canDrill && d.onDrill ? (
        <button type="button" className="lc-canvas-node__drill" onClick={drillScene}>
          <PencilLine size={13} aria-hidden="true" />
          {tr(S.enterEpisode)}
        </button>
      ) : null}

      {d.kind === "missing" ? (
        <div className="lc-canvas-node__meta">
          <Badge size="sm" variant="tonal" tone="danger">
            {tr(S.missingTarget)}
          </Badge>
          <span className="lc-canvas-node__id">{d.sceneId}</span>
        </div>
      ) : null}

      {d.kind === "missing" && d.onCreateMissing ? (
        <button type="button" className="lc-canvas-node__create" onClick={createMissing}>
          <Plus size={13} aria-hidden="true" />
          {tr(S.createTarget)}
        </button>
      ) : null}

      <Handle type="source" position={Position.Right} className="lc-canvas-handle" />
    </div>
  );
}

/** Memoised — ReactFlow re-renders nodes often; data is referentially stable. */
export const StoryNode = memo(StoryNodeImpl);
